import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withLastLocation } from 'react-router-last-location';
import _isEmpty from 'lodash/isEmpty';
import Preloader from '@omilia/preloader';

import { paginationOptionsType, projectType, xpackType } from 'types';
import {
  getXpacks,
  deleteXpack,
  addXpack,
  cancelAddXpack,
  updateXpack,
  cancelUpdateXpack,
  downloadXpack,
  overrideAllXpackIntents,
  disableAllXpackIntents,
} from 'store/services/xpacks/actions';
// TODO: get required selectors only
import * as selectors from 'store/services/xpacks/selectors';
import {
  getProject,
  getProjectIsLoading,
} from 'store/services/project/selectors';

import Button from 'components/Button';
import Pagination from 'components/Pagination';

import XpacksList from './components/XpacksList';
import NoXpacks from './components/NoXpacks';
import DeleteXpackModal from './components/DeleteXpackModal';
import UpdateXpackModal from './components/UpdateXpackModal';
import ImportXpackModal from './components/ImportXpackModal';

import './style.scss';

/* eslint-disable react/destructuring-assignment */

export class Xpacks extends Component {
  static propTypes = {
    addXpack: PropTypes.func.isRequired,
    cancelAddXpack: PropTypes.func.isRequired,
    cancelUpdateXpack: PropTypes.func.isRequired,
    count: PropTypes.number.isRequired,
    deleteXpack: PropTypes.func.isRequired,
    disableAllXpackIntents: PropTypes.func.isRequired,
    disableAllXpackIntentsIsLoading: PropTypes.bool.isRequired,
    downloadXpack: PropTypes.func.isRequired,
    getXpacks: PropTypes.func.isRequired,
    isAdding: PropTypes.bool.isRequired,
    isDeleting: PropTypes.bool.isRequired,
    isDownloading: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    overrideAllXpackIntents: PropTypes.func.isRequired,
    overrideAllXpackIntentsIsLoading: PropTypes.bool.isRequired,
    paginationOptions: paginationOptionsType.isRequired,
    project: projectType.isRequired,
    projectIsLoading: PropTypes.bool.isRequired,
    selectedSortOption: PropTypes.string.isRequired,
    updateXpack: PropTypes.func.isRequired,
    xpacks: PropTypes.arrayOf(xpackType).isRequired,
    lastLocation: PropTypes.shape({ pathname: PropTypes.string.isRequired }),
  };

  state = {
    isImportXpackModalOpen: false,
    isUpdateXpackModalOpen: false,
    isDeleteXpackModalOpen: false,
    selectedXpack: null,
  };

  componentDidMount() {
    this.props.getXpacks();
  }

  handleOpenImportXpackModal = () => {
    this.setState({
      isImportXpackModalOpen: true,
    });
  };

  handleCloseImportXpackModal = () => {
    this.props.cancelAddXpack();
    this.setState({
      isImportXpackModalOpen: false,
    });
  };

  handleOpenUpdateXpackModal = selectedXpack => {
    this.setState({
      isUpdateXpackModalOpen: true,
      selectedXpack,
    });
  };

  handleCloseUpdateXpackModal = () => {
    this.setState({
      isUpdateXpackModalOpen: false,
      selectedXpack: null,
    });
  };

  handleOpenDeleteXpackModal = selectedXpack => {
    this.setState({
      isDeleteXpackModalOpen: true,
      selectedXpack,
    });
  };

  handleCloseDeleteXpackModal = () => {
    this.setState({
      isDeleteXpackModalOpen: false,
      selectedXpack: null,
    });
  };

  handleUpdateXpack = async data => {
    try {
      await this.props.updateXpack(this.state.selectedXpack, data);

      this.handleCloseUpdateXpackModal();
    } catch (err) {
      throw err;
    }
  };

  handleDeleteXpack = async () => {
    try {
      await this.props.deleteXpack(this.state.selectedXpack);

      this.handleCloseDeleteXpackModal();
    } catch (err) {
      throw err;
    }
  };

  handleAddXpack = async file => {
    try {
      await this.props.addXpack({ file });
    } catch (err) {
      throw err;
    }

    this.handleCloseImportXpackModal();
  };

  renderPreloader = (isLoading, projectIsLoading) => {
    if (isLoading || projectIsLoading) {
      return <Preloader />;
    }

    return null;
  };

  renderNoXpacks(isLoading, projectIsLoading, xpacks, project) {
    if (
      xpacks.length === 0 &&
      !isLoading &&
      !projectIsLoading &&
      !_isEmpty(project)
    ) {
      return <NoXpacks onImport={this.handleOpenImportXpackModal} />;
    }

    return null;
  }

  renderXpacks(xpacks) {
    if (xpacks.length) {
      const {
        selectedSortOption,
        getXpacks,
        isDownloading,
        downloadXpack,
        overrideAllXpackIntentsIsLoading,
        overrideAllXpackIntents,
        disableAllXpackIntentsIsLoading,
        disableAllXpackIntents,
      } = this.props;

      return (
        <XpacksList
          disableAllXpackIntentsIsLoading={disableAllXpackIntentsIsLoading}
          getXpacks={getXpacks}
          isDownloading={isDownloading}
          overrideAllXpackIntentsIsLoading={overrideAllXpackIntentsIsLoading}
          selectedSortOption={selectedSortOption}
          xpacks={xpacks}
          onDelete={this.handleOpenDeleteXpackModal}
          onDisableAllXpackIntents={disableAllXpackIntents}
          onDownload={downloadXpack}
          onOverrideAllXpackIntents={overrideAllXpackIntents}
          onUpdate={this.handleOpenUpdateXpackModal}
        />
      );
    }

    return null;
  }

  renderPagination(xpacks) {
    if (xpacks.length) {
      const { count, paginationOptions, getXpacks } = this.props;

      return (
        <Pagination
          count={count}
          getItems={getXpacks}
          paginationOptions={paginationOptions}
        />
      );
    }

    return null;
  }

  render() {
    const {
      isImportXpackModalOpen,
      isUpdateXpackModalOpen,
      isDeleteXpackModalOpen,
    } = this.state;
    const {
      xpacks,
      isLoading,
      projectIsLoading,
      project,
      isDeleting,
      isAdding,
      isUpdating,
      cancelUpdateXpack,
    } = this.props;

    return (
      <div data-test="xPacks" styleName="xpacks">
        {isImportXpackModalOpen && (
          <ImportXpackModal
            isLoading={isAdding}
            onClose={this.handleCloseImportXpackModal}
            onImport={this.handleAddXpack}
          />
        )}

        {isUpdateXpackModalOpen && (
          <UpdateXpackModal
            isLoading={isUpdating}
            onCancelUpdating={cancelUpdateXpack}
            onClose={this.handleCloseUpdateXpackModal}
            onUpdate={this.handleUpdateXpack}
          />
        )}

        {isDeleteXpackModalOpen && (
          <DeleteXpackModal
            isFetching={isDeleting}
            onCloseRequest={this.handleCloseDeleteXpackModal}
            onDelete={this.handleDeleteXpack}
          />
        )}

        <div styleName="header">
          <h3 data-test="title" styleName="title">
            xPacks
          </h3>
          <Button
            data-test="import-xpack-button"
            onClick={this.handleOpenImportXpackModal}
          >
            Import xPack
          </Button>
        </div>
        <div styleName="container">
          {this.renderPreloader(isLoading, projectIsLoading)}
          {this.renderNoXpacks(isLoading, projectIsLoading, xpacks, project)}
          {this.renderXpacks(xpacks)}
          {this.renderPagination(xpacks)}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  xpacks: selectors.getXpacks(state),
  count: selectors.getXpacksCount(state),
  isLoading: selectors.getIsLoading(state),
  isDeleting: selectors.getIsDeleting(state),
  isDownloading: selectors.getIsDownloading(state),
  isAdding: selectors.getIsAdding(state),
  isUpdating: selectors.getIsUpdating(state),
  paginationOptions: selectors.getPaginationOptions(state),
  selectedSortOption: selectors.getSelectedSortOption(state),
  project: getProject(state),
  projectIsLoading: getProjectIsLoading(state),
  overrideAllXpackIntentsIsLoading: selectors.getOverrideAllXpackIntentsIsLoading(
    state,
  ),
  disableAllXpackIntentsIsLoading: selectors.getDisableAllXpackIntentsIsLoading(
    state,
  ),
});

const mapDispatchToProps = {
  getXpacks,
  deleteXpack,
  updateXpack,
  cancelUpdateXpack,
  addXpack,
  cancelAddXpack,
  downloadXpack,
  overrideAllXpackIntents,
  disableAllXpackIntents,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLastLocation,
)(Xpacks);
